import React, { useRef, useEffect, useState } from "react";
import { X, Search, Package } from "lucide-react";
import { useLazyGetProductListQuery } from "../../State/Api/AddPurchaseApiSlice";

export default function ProductSelectionModal({ open, onClose, onSelect }) {
  const [searchText, setSearchText] = useState("");
  const [products, setProducts] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  const [triggerGetProducts, { isFetching }] = useLazyGetProductListQuery();

  // Autofocus on input when modal opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  // 🔹 Debounced API call for live product search
  useEffect(() => {
    if (!searchText.trim()) {
      setProducts([]);
      return;
    }

    const fetchProducts = async () => {
      try {
        const pharmaId = localStorage.getItem("userUniqueID");
        const result = await triggerGetProducts({
          pharmaId,
          productname: searchText.trim(),
        }).unwrap();

        console.log("✅ Fetched products:", result);

        setProducts(Array.isArray(result) ? result : []);
      } catch (err) {
        console.error("❌ Error fetching products:", err);
        setProducts([]);
      }
    };

    const debounceTimer = setTimeout(fetchProducts, 400);
    return () => clearTimeout(debounceTimer);
  }, [searchText, triggerGetProducts]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((a) => Math.min(a + 1, Math.max(products.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && products[activeIdx]) {
      e.preventDefault();
      onSelect(products[activeIdx]);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          width: "90%",
          maxWidth: "900px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px",
            borderBottom: "2px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: 700,
                margin: 0,
                color: "#1f2937",
              }}
            >
              Select a Product
            </h2>
            <p
              style={{
                fontSize: "13px",
                color: "#999",
                margin: "4px 0 0 0",
              }}
            >
              Search and select a medicine from your list
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#fee2e2",
              border: "none",
              borderRadius: "6px",
              width: "36px",
              height: "36px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={18} color="#dc2626" />
          </button>
        </div>

        {/* Search Input */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            SEARCH PRODUCT
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              position: "relative",
            }}
          >
            <Search
              size={16}
              color="#999"
              style={{ position: "absolute", left: "12px" }}
            />
            <input
              ref={inputRef}
              placeholder="Search by Medicine Name or Company"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              style={{
                flex: 1,
                padding: "10px 12px 10px 40px",
                border: "2px solid #10b981",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
              }}
            />
          </div>
        </div>

        {/* Product Table */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {isFetching ? (
            <div
              style={{"padding":"48px 24px","textAlign":"center","display":"flex","alignItems":"center","justifyContent":"center","color":"rgb(153, 153, 153)","flexDirection":"column"}}
            >
              <Package
                size={40}
                color="#d1d5db"
                style={{ marginBottom: "12px" }}
              />
              <p style={{ margin: 0, fontSize: "14px" }}>Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div
              style={{"padding":"48px 24px","textAlign":"center","display":"flex","alignItems":"center","justifyContent":"center","color":"rgb(153, 153, 153)","flexDirection":"column"}}
            >
              <Package
                size={40}
                color="#d1d5db"
                style={{ marginBottom: "12px" }}
              />
              <p style={{ margin: 0, fontSize: "14px" }}>
                {searchText.trim()
                  ? "No products found"
                  : "Start typing to search for products"}
              </p>
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
              }}
            >
              <thead style={{ position: "sticky", top: 0 }}>
                <tr
                  style={{
                    background: "#f9fafb",
                    borderBottom: "2px solid #e5e7eb",
                  }}
                >
                  <th style={thStyle}>PRODUCT NAME</th>
                  <th style={thCenter}>PACK</th>
                  <th style={thRight}>MRP</th>
                  <th style={thRight}>SALE RATE</th>
                  <th style={thCenter}>GST %</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, idx) => (
                  <tr
                    key={product.productID || idx}
                    onClick={() => {
                      onSelect(product);
                      onClose();
                    }}
                    style={{
                      background:
                        idx === activeIdx
                          ? "#f0fdf4"
                          : idx % 2 === 0
                          ? "#fff"
                          : "#fafbfc",
                      borderBottom: "1px solid #e5e7eb",
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                  >
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontWeight: 600, color: "#1f2937" }}>
                        {product.productName}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          marginTop: "2px",
                        }}
                      >
                        {product.cmpnyName}
                      </div>
                    </td>
                    <td style={tdCenter}>{product.unitsPerPack ?? "-"}</td>
                    <td style={tdRight}>
                      ₹{product.mrp?.toLocaleString("en-IN") ?? "-"}
                    </td>
                    <td style={tdRight}>
                      <span style={{ color: "#10b981", fontWeight: 700 }}>
                        ₹{product.saleRate?.toLocaleString("en-IN") ?? "-"}
                      </span>
                    </td>
                    <td style={tdCenter}>{product.gst ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}

const thStyle = {
  padding: "12px 16px",
  textAlign: "left",
  fontWeight: 700,
  color: "#374151",
};

const thCenter = {
  ...thStyle,
  textAlign: "center",
};

const thRight = {
  ...thStyle,
  textAlign: "right",
};

const tdCenter = {
  padding: "12px 16px",
  textAlign: "center",
  color: "#1f2937",
  fontWeight: 600,
};

const tdRight = {
  padding: "12px 16px",
  textAlign: "right",
  color: "#6b7280",
};
